import React from "react";
import { Button } from "subframe/components/Button";
import { IconWithBackground } from "subframe/components/IconWithBackground";
import { Feed } from "subframe/components/Feed";
import { ExitSurveyAPIResponse } from "modules/Retention/types/exitSurvey";
import { useNavigate, useParams } from "react-router-dom";
import RETENTION_ROUTES, { getEmployeeProfileRoute } from "modules/Retention/constants/routes";
import * as SubframeCore from "@subframe/core";
import { formatLocalDate } from "utils/helper";
import useUser from "hooks/userHooks";

interface ExitSurveyLogProps {
  data: ExitSurveyAPIResponse;
}

interface LogEntry {
  type: 'employment_ended' | 'survey_completed' | 'employee_survey' | 'hrbp_survey';
  title: string;
  date: string;
  icon: SubframeCore.IconName;
  hasConnector: boolean;
  timestamp?: number;
}

interface SurveyEntry extends LogEntry {
  timestamp: number;
}

export const ExitSurveyLog: React.FC<ExitSurveyLogProps> = ({ data }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const employeeId = parseInt(id || '0', 10);
  const { employee, hrbp } = data;
  
  // Build log entries based on available data
  const buildLogEntries = (): LogEntry[] => {
    const entries: LogEntry[] = [];
    
    // Employment ended (always at bottom, even if null)
    entries.push({
      type: 'employment_ended',
      title: 'Employment ended',
      date: data.termination_date ? formatLocalDate(data.termination_date) : 'N/A',
      icon: 'FeatherUserX' as SubframeCore.IconName,
      hasConnector: false
    });
    
    // Check if any survey is completed (employee OR HRBP)
    const employeeCompleted = employee.completed;
    const hrbpCompleted = hrbp.completed;
    
    if (employeeCompleted || hrbpCompleted) {
      // Add general "Completed survey" entry
      let mostRecentDate = '';
      
      if (employeeCompleted && hrbpCompleted) {
        const employeeDate = new Date(employee.responses[employee.responses.length - 1].updated_at);
        const hrbpDate = new Date(hrbp.responses[hrbp.responses.length - 1].updated_at);
        mostRecentDate = employeeDate > hrbpDate ? formatLocalDate(employee.responses[employee.responses.length - 1].updated_at) : formatLocalDate(hrbp.responses[hrbp.responses.length - 1].updated_at);
      } else if (employeeCompleted) {
        mostRecentDate = formatLocalDate(employee.responses[employee.responses.length - 1].updated_at);
      } else if (hrbpCompleted) {
        mostRecentDate = formatLocalDate(hrbp.responses[hrbp.responses.length - 1].updated_at);
      }
      
      entries.push({
        type: 'survey_completed',
        title: 'Completed survey',
        date: mostRecentDate,
        icon: 'FeatherSend' as SubframeCore.IconName,
        hasConnector: true
      });
      
      // Add individual survey entries in chronological order
      const surveyEntries: SurveyEntry[] = [];
      
      if (employeeCompleted) {
        const employeeDate = new Date(employee.responses[employee.responses.length - 1].updated_at);
        surveyEntries.push({
          type: 'employee_survey',
          title: 'Completed survey - Employee',
          date: formatLocalDate(employee.responses[employee.responses.length - 1].updated_at),
          icon: 'FeatherCheckSquare' as SubframeCore.IconName,
          hasConnector: true,
          timestamp: employeeDate.getTime()
        });
      }
      
      if (hrbpCompleted) {
        const hrbpDate = new Date(hrbp.responses[hrbp.responses.length - 1].updated_at);
        surveyEntries.push({
          type: 'hrbp_survey',
          title: 'Completed survey - HRBP',
          date: formatLocalDate(hrbp.responses[hrbp.responses.length - 1].updated_at),
          icon: 'FeatherCheckSquare' as SubframeCore.IconName,
          hasConnector: true,
          timestamp: hrbpDate.getTime()
        });
      }
      
      // Sort individual survey entries by timestamp (earliest first)
      surveyEntries.sort((a, b) => a.timestamp - b.timestamp);
      entries.push(...surveyEntries);
    }
    
    // Reverse the array to show employment ended at bottom
    return entries.reverse();
  };
  
  const logEntries = buildLogEntries();

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-144 items-center justify-between">
        <span className="text-section-header font-section-header text-default-font">
          Exit Survey Log
        </span>
      </div>
      <div className="flex items-center gap-4">
        <Button
          disabled={true}
          icon="FeatherUserCheck"
        >
          Log Follow-up
        </Button>
        <Button
          variant="brand-secondary"
          icon="FeatherUserCircle"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              const profileRoute = getEmployeeProfileRoute(user?.agency_id);
              navigate(profileRoute.replace(":id", employeeId.toString()))
          }}
        >
          Employee Profile
        </Button>
      </div>
      <div className="flex w-full flex-col items-start gap-4">
        <div className="flex w-full max-w-[768px] flex-col items-start gap-4 px-6">
          <Feed>
            {logEntries.map((entry, index) => (
              <div key={`${entry.type}-${index}`} className="flex w-full grow shrink-0 basis-0 items-start gap-4">
                <div className="flex flex-col items-center self-stretch">
                  <div className="flex flex-col items-start gap-1">
                    <IconWithBackground
                      variant="neutral"
                      size="small"
                      icon={entry.icon}
                    />
                  </div>
                  {entry.hasConnector && index < logEntries.length - 1 && (
                    <div className="flex w-0.5 grow shrink-0 basis-0 flex-col items-start gap-2 bg-neutral-border" />
                  )}
                </div>
                <div className="flex grow shrink-0 basis-0 flex-col items-end gap-2 pt-1 pb-7">
                  <div className="flex w-full flex-wrap items-center gap-2">
                    <div className="flex grow shrink-0 basis-0 flex-wrap items-start gap-1">
                      <span className="text-body-bold font-body-bold text-default-font">
                        {entry.title}
                      </span>
                    </div>
                    <span className="text-label font-label text-subtext-color">
                      {entry.date}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </Feed>
        </div>
      </div>
    </div>
  );
};